import {
  ActorRole,
  AssessmentStageKind,
  CognitiveKind,
  CognitiveMode,
  InterviewStageFocus,
  ProductSolution,
} from 'generated/graphql';
import { actorRoleLabels, defaultActorRoles } from 'helpers/actorRoles';
import { TMultiselectOption } from '@spotted-zebra-uk/ui-components';

const professionalHiringActorRoles = [
  ActorRole.RecruiterOrTalentAcquisitionPartner,
  ActorRole.HiringManager,
  ActorRole.Interviewer,
  ActorRole.HeadOfPeopleOrTalent,
  ActorRole.EarlyCareerTeamMember,
  ActorRole.AssessorOrInterviewer,
  ActorRole.ReskillingTeamMember,
  ActorRole.LineManager,
];

const reskillingActorRoles = [
  ActorRole.ReskillingTeamMember,
  ActorRole.HiringManager,
  ActorRole.LineManager,
  ActorRole.Interviewer,
  ActorRole.HeadOfPeopleOrTalent,
  ActorRole.RecruiterOrTalentAcquisitionPartner,
  ActorRole.EarlyCareerTeamMember,
  ActorRole.AssessorOrInterviewer,
];

const earlyCareersActorRoles = [
  ActorRole.EarlyCareerTeamMember,
  ActorRole.AssessorOrInterviewer,
  ActorRole.HeadOfPeopleOrTalent,
  ActorRole.RecruiterOrTalentAcquisitionPartner,
  ActorRole.HiringManager,
  ActorRole.Interviewer,
  ActorRole.ReskillingTeamMember,
  ActorRole.LineManager,
];

const productSolutionOrdering: Record<ProductSolution, ActorRole[]> = {
  [ProductSolution.ProfessionalHiring]: professionalHiringActorRoles,
  [ProductSolution.Reskilling]: reskillingActorRoles,
  [ProductSolution.EarlyCareerHiring]: earlyCareersActorRoles,
  [ProductSolution.Learning]: defaultActorRoles,
  [ProductSolution.SuccessionPlanning]: defaultActorRoles,
  [ProductSolution.VolumeHiring]: defaultActorRoles,
};

export function getOrderedActorRoles(
  productSolution: ProductSolution | undefined | null
): ActorRole[] {
  if (!productSolution) {
    return defaultActorRoles;
  }

  return productSolutionOrdering[productSolution];
}

export function parseActorRolesForSelect(
  productSolution: ProductSolution | undefined | null
): TMultiselectOption[] {
  return getOrderedActorRoles(productSolution).map(actorRole => ({
    label: actorRoleLabels[actorRole],
    value: actorRole,
  }));
}

export const stageKindLabel = (kind: AssessmentStageKind): string => {
  switch (kind) {
    case AssessmentStageKind.AiInterview:
      return 'AI Interview';
    default:
      return 'Assessment';
  }
};

export const MIN_TEST_QUESTIONS = {
  [CognitiveMode.InDepth]: {
    [CognitiveKind.Numerical]: 4,
    [CognitiveKind.Inductive]: 8,
    [CognitiveKind.Verbal]: 4,
  },
  [CognitiveMode.Streamlined]: {
    [CognitiveKind.Numerical]: 2,
    [CognitiveKind.Inductive]: 4,
    [CognitiveKind.Verbal]: 2,
  },
};

export const MAX_TEST_QUESTIONS = {
  [CognitiveMode.InDepth]: {
    [CognitiveKind.Numerical]: 10,
    [CognitiveKind.Inductive]: 18,
    [CognitiveKind.Verbal]: 10,
  },
  [CognitiveMode.Streamlined]: {
    [CognitiveKind.Numerical]: 10,
    [CognitiveKind.Inductive]: 18,
    [CognitiveKind.Verbal]: 10,
  },
};

export const MIN_PERSONALITY_QUESTIONS_PER_TRAIT = 1;
export const MAX_PERSONALITY_QUESTIONS_PER_TRAIT = 18;

export const cognitiveKindLabel = (kind: CognitiveKind): string => {
  switch (kind) {
    case CognitiveKind.Inductive:
      return 'Inductive';
    case CognitiveKind.Numerical:
      return 'Numerical';
    case CognitiveKind.Verbal:
      return 'Verbal';
    default:
      return kind;
  }
};

export const cognitiveTestLabel = (kind: CognitiveKind): string =>
  `Cognitive ${cognitiveKindLabel(kind).toLowerCase()} test`;

export const STAGE_NAME_MAX_LENGTH = 80;

export const INTERVIEW_STAGE_DURATION_DEFAULT_VALUE = 3600;
export const INTERVIEW_STAGE_FOCUS_DEFAULT_VALUE =
  InterviewStageFocus.CoreSkills;
export const INTERVIEW_STAGE_NAME_DEFAULT_VALUE = 'Interview';

export const INTERVIEW_STAGE_DEFAULT_VALUES = {
  duration: INTERVIEW_STAGE_DURATION_DEFAULT_VALUE,
  focus: INTERVIEW_STAGE_FOCUS_DEFAULT_VALUE,
  name: INTERVIEW_STAGE_NAME_DEFAULT_VALUE,
};

export function getInterviewStageDurationLabel(seconds: number) {
  return `${Math.round(seconds / 60)} minutes`;
}

export function getInterviewStageFocusLabel(focus: InterviewStageFocus) {
  return focus
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}
